import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Avatar,
    Container,
    Paper,
    Button,
    Tabs,
    Tab,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Divider,
    Stack,
    Chip,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from "../context/AuthContext";
import { ErrorContext } from "../context/ErrorContext";
import { useParams, useSearchParams } from 'react-router-dom';
import { createAxiosInstance } from "../api/axios";

import { getMultipleResumeRankings } from '../api/resumeRankingProfile';
import { rankResumeWithErrorHandling } from '../api/resumeRanking';
import GradeScoreDisplay from './GradeScoreDisplay';

// Import tab components - Mercor-style redesign
import ResumeTab from './ProfileTabs/ResumeTab';
import LocationWorkAuthTab from './ProfileTabs/LocationWorkAuthTab';
import AvailabilityTab from './ProfileTabs/AvailabilityTab';
import WorkPreferencesTab from './ProfileTabs/WorkPreferencesTab';
import CommunicationsTab from './ProfileTabs/CommunicationsTab';
import AccountTab from './ProfileTabs/AccountTab';
import { profileCache } from './ProfileTabs/profileCache';

/* Lines 19-35 omitted */

const ProfileComponent = () => {
    const { user, loading, refetchUser } = useContext(AuthContext);
    const { setError } = useContext(ErrorContext);
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [userProfile, setUserProfile] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [profileImageUrl, setProfileImageUrl] = useState(null)
    const [imageLoading] = useState(false)

    const [resumeRankings, setResumeRankings] = useState({});
    const [rankingsLoading, setRankingsLoading] = useState(false);
    const [tokenProcessed, setTokenProcessed] = useState(false);
    
    // Resume upload states
    const [uploadingResume, setUploadingResume] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const resumeInputRef = useRef(null);
    
    // Resume upload modal states
    const [showResumeUploadModal, setShowResumeUploadModal] = useState(false);
    const [resumeModalStep, setResumeModalStep] = useState('initial'); // 'initial', 'uploading', 'parsing', 'analyzing', 'completed'
    const [currentResumeId, setCurrentResumeId] = useState(null);
    const [resumeRankingData, setResumeRankingData] = useState(null);
    const [resumeStatus, setResumeStatus] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [applyingToProfile, setApplyingToProfile] = useState(false); // eslint-disable-line no-unused-vars
    const [parsedDataForResumeTab, setParsedDataForResumeTab] = useState(null);
    
    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // Tab name to index mapping - Mercor-style tabs
    const tabFragments = ['resume', 'location', 'availability', 'preferences', 'communications', 'account'];
    const fragmentToTab = {
        'resume': 0,
        'location': 1,
        'availability': 2,
        'preferences': 3,
        'communications': 4,
        'account': 5,
    };

    // Profile picture states
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const fileInputRef = useRef(null);

    const authAxios = useMemo(() => {
        return createAxiosInstance(null);
    }, []);

    const profileAPI = {
        getExperience: (userId) => authAxios.get('/experiences/'),
        getEducation: (userId) => authAxios.get('/education/'),
        getProjects: (userId) => authAxios.get('/projects/'),
        getPersonalInfo: () => authAxios.get('/self/'),
    };

    // eslint-disable-next-line no-unused-vars
    const InfoRow = ({ label, value }) => (
        <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
                {value || <i style={{ color: '#888' }}>Not Provided</i>}
            </Typography>
        </Grid>
    );

    const getInitials = (firstName, lastName) => {
        if (!firstName && !lastName) return '';
        const first = firstName ? firstName[0] : '';
        const last = lastName ? lastName[0] : '';
        return (first + last).toUpperCase();
    };

    // Note: profile_picture_url is now a direct, public URL to the image in the bucket.
    // We no longer fetch the blob via API. Use the URL directly as the image src.
    // Helper to set/unset the image URL.
    const setProfileImageFromUrl = (imageUrl) => {
        if (!imageUrl) {
            setProfileImageUrl(null);
            return;
        }
        setProfileImageUrl(imageUrl);
    };

    // Profile picture handlers
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
            if (!validTypes.includes(file.type)) {
                setError("Please select a valid image file (JPG, PNG, or WebP)");
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setError("File size must be less than 5MB");
                return;
            }

            setSelectedFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setShowPreview(true);
        }
    };

    const handleUploadProfilePicture = async () => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("profile_picture", selectedFile);

            const response = await authAxios.post("/profile-picture/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setShowPreview(false);
            setSelectedFile(null);

            // Clean up preview URL
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }

            // Immediately set the new profile picture URL from response (with cache-busting)
            if (response.data.profile_picture_url) {
                const newImageUrl = response.data.profile_picture_url + 
                    (response.data.profile_picture_url.includes('?') ? '&' : '?') + 
                    't=' + Date.now();
                setProfileImageUrl(newImageUrl);
                
                // Update userProfile state with new URL
                setUserProfile(prev => ({
                    ...prev,
                    profile_picture_url: response.data.profile_picture_url,
                }));
                
                // Invalidate cache and refresh AuthContext to update Navbar
                if (user?.id) {
                    profileCache.invalidate(user.id);
                    // Also refresh auth context to update the Navbar avatar
                    refetchUser();
                }
            }
        } catch (error) {
            console.error("Upload error:", error);
            // Handle 413 - file too large
            if (error.response?.status === 413) {
                setError("Image size exceeds the 5 MB limit. Please choose a smaller image.");
            } else {
                setError(error.response?.data?.message || "Failed to upload profile picture");
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteProfilePicture = async () => {
        try {
            await authAxios.delete("/profile-picture/");
            setProfileImageUrl(null);
            
            // Update userProfile state
            setUserProfile(prev => ({
                ...prev,
                profile_picture_url: null,
            }));
            
            // Invalidate cache and refresh AuthContext to update Navbar
            if (user?.id) {
                profileCache.invalidate(user.id);
                refetchUser();
            }
        } catch (error) {
            console.error("Delete error:", error);
            setError(error.response?.data?.message || "Failed to remove profile picture");
        }
    };

    // Resume upload functions
    const uploadResume = async (file) => {
        setUploadingResume(true);
        setResumeModalStep('uploading');
        setUploadStatus('Uploading resume...');
        try {
            const formData = new FormData();
            formData.append('resume', file);

            console.log('Uploading file:', file.name, 'Size:', file.size);

            const response = await authAxios.post('/user-resume/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            console.log('Upload response:', response.data);

            if (!response.data.id) {
                throw new Error('No resume ID returned from upload');
            }

            const resumeId = response.data.id;
            setCurrentResumeId(resumeId);
            setUploadStatus('Resume uploaded successfully! Starting analysis...');
            
            // Automatically trigger parsing after upload
            setResumeModalStep('parsing');
            try {
                await enrichResume(resumeId);
                // Start status polling after successful enrich call
                startStatusPolling(resumeId);
            } catch (parseError) {
                console.error('Auto-parse failed:', parseError);
                // If parse failed but it's already being processed, still start polling
                if (parseError.response?.data?.error?.includes('already being processed')) {
                    console.log('Resume is already being processed, will continue checking status...');
                    startStatusPolling(resumeId);
                } else {
                    throw parseError;
                }
            }
            
            return resumeId;
        } catch (error) {
            console.error('Error uploading resume:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to upload resume. Please try again.';
            setError(errorMessage);
            setUploadStatus('');
            setUploadingResume(false);
            setResumeModalStep('initial');
            throw error;
        }
    };

    const enrichResume = async (resumeId) => {
        try {
            console.log('Attempting to enrich resume with ID:', resumeId);
            setResumeModalStep('parsing');
            setUploadStatus('Processing resume...');
            const response = await authAxios.post(`/user-resume/${resumeId}/enrich/`);
            console.log('Enrich response:', response.data);
            return true;
        } catch (error) {
            console.error('Error enriching resume:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            
            // Handle specific case where resume is already being processed
            if (error.response?.data?.error === 'Resume is already being processed') {
                console.log('Resume is already being processed, will continue checking status...');
                setUploadStatus('Resume is already being processed. Checking status...');
                return true;
            }

            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                `Failed to start resume parsing`;
            throw new Error(errorMessage);
        }
    };

    const startStatusPolling = (resumeId) => {
        let pollCount = 0;
        const maxPolls = 60; // Maximum 5 minutes of polling (60 * 5 seconds)
        
        const pollStatus = async () => {
            try {
                pollCount++;
                console.log(`Polling status attempt ${pollCount}/${maxPolls}`);
                
                const response = await authAxios.get(`/user-resume/${resumeId}/status/`);
                const status = response.data;
                setResumeStatus(status);
                console.log('Resume status:', status);
                
                if (status.status === 'ENRICHED') {
                    console.log('Resume processing completed (ENRICHED)!');
                    setResumeModalStep('analyzing');
                    setUploadStatus('Resume processed successfully! Analyzing...');
                    
                    // Fetch parsed data and trigger ranking
                    try {
                        await fetchParsedDataAndRank(resumeId);
                        setResumeModalStep('completed');
                        setUploadStatus('Resume analysis completed!');
                        setTimeout(() => {
                            setUploadStatus('');
                            setUploadingResume(false);
                            // Reload profile data to show new resume
                            loadOwnProfileData(true);
                        }, 2000);
                    } catch (fetchError) {
                        console.error('Failed to fetch parsed data:', fetchError);
                        const errorMessage = fetchError.message || 'Failed to fetch parsed resume data';
                        setError(errorMessage);
                        setUploadStatus('');
                        setUploadingResume(false);
                        setResumeModalStep('initial');
                    }
                    
                    return; // Stop polling
                } else if (status.status === 'failed' || status.status === 'ENRICH_FAILED') {
                    console.log('Resume processing failed!');
                    const errorMessage = status.error_message || 'Resume processing failed. Please try again.';
                    setError(errorMessage);
                    setUploadStatus('');
                    setUploadingResume(false);
                    setResumeModalStep('initial');
                    return; // Stop polling
                } else if (pollCount >= maxPolls) {
                    console.log('Status polling timeout reached');
                    setError('Resume processing is taking longer than expected. Please check back later.');
                    setUploadStatus('');
                    setUploadingResume(false);
                    setResumeModalStep('initial');
                    return; // Stop polling
                } else {
                    // Continue polling if status is "pending" or "processing"
                    console.log(`Resume status: ${status.status}, continuing to poll...`);
                    setResumeModalStep('parsing');
                    setUploadStatus(`Processing resume... (${status.status})`);
                    setTimeout(pollStatus, 5000); // Poll every 5 seconds
                }
            } catch (error) {
                console.error('Error during status polling:', error);
                pollCount++;
                
                if (pollCount >= maxPolls) {
                    const errorMessage =
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        'Failed to check resume status. Please try again.';
                    setError(errorMessage);
                    setUploadStatus('');
                    setUploadingResume(false);
                    setResumeModalStep('initial');
                    return; // Stop polling
                }
                
                // Continue polling even if there's an error, but with a longer interval
                setTimeout(pollStatus, 10000); // Poll every 10 seconds on error
            }
        };
        
        // Start the first poll after a short delay
        setTimeout(pollStatus, 2000);
    };

    const fetchParsedDataAndRank = async (resumeId) => {
        try {
            console.log('Fetching parsed data for resume ID:', resumeId);
            const parsedDataResponse = await authAxios.get(`/user-resume/${resumeId}/parsed_data/`);
            console.log('Parsed data retrieved successfully');
            setParsedData(parsedDataResponse.data);
            
            // Start resume ranking analysis
            setUploadStatus('Analyzing your resume quality...');
            
            try {
                const rankingResult = await rankResumeWithErrorHandling(resumeId, null);

                if (!rankingResult.success) {
                    console.error('Resume ranking failed:', rankingResult.error);
                    throw new Error(rankingResult.error || 'Failed to analyze resume ranking');
                }
                
                console.log('Resume ranking completed successfully');
                // Save the ranking data for display in modal
                setResumeRankingData(rankingResult.data);
            } catch (error) {
                console.error('Resume ranking error:', error);
                // Don't throw here - resume upload was successful, just ranking failed
                setError(`Resume uploaded successfully, but quality analysis failed: ${error.message}`);
            }

            return true;
        } catch (error) {
            console.error('Error fetching parsed data:', error);
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to fetch parsed resume data';
            throw new Error(errorMessage);
        }
    };

    const handleResumeFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            setError('Please upload a PDF file');
            return;
        }

        // Validate file size (e.g., max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            setError('File size must be less than 10MB');
            return;
        }

        // Open modal and start upload
        setShowResumeUploadModal(true);
        setResumeModalStep('uploading');
        setResumeRankingData(null);

        try {
            await uploadResume(file);
        } catch (error) {
            console.error('Resume upload failed:', error);
            setShowResumeUploadModal(false);
        }

        // Clear the input so the same file can be selected again
        if (event.target) {
            event.target.value = '';
        }
    };

    // Helper function to parse gaps data - converts any structure to string array
    const parseGapsData = (gapsData) => {
        if (!gapsData) return [];
        
        // Helper to safely convert any value to string
        const toSafeString = (item) => {
            if (item === null || item === undefined) return '';
            if (typeof item === 'string') return item;
            if (typeof item === 'number' || typeof item === 'boolean') return String(item);
            // For objects, try to extract 'name' property first, otherwise stringify
            if (typeof item === 'object') {
                if (item.name && typeof item.name === 'string') {
                    return item.name;
                }
                // Try other common text properties
                if (item.title && typeof item.title === 'string') {
                    return item.title;
                }
                if (item.label && typeof item.label === 'string') {
                    return item.label;
                }
                // Last resort: stringify but make it readable
                return JSON.stringify(item);
            }
            return String(item);
        };
        
        try {
            // If it's a string, try to parse it first
            if (typeof gapsData === 'string') {
                try {
                    const parsed = JSON.parse(gapsData.replace(/'/g, '"'));
                    // Recursively call with parsed data
                    return parseGapsData(parsed);
                } catch {
                    // If parsing fails, try to extract quoted strings
                    const matches = gapsData.match(/'([^']+)'/g);
                    if (matches) {
                        return matches.map(match => match.replace(/'/g, ''));
                    }
                    // Return as single item
                    return [gapsData];
                }
            }
            
            // If it's an array, convert each item to string
            if (Array.isArray(gapsData)) {
                return gapsData
                    .map(toSafeString)
                    .filter(str => str && str.trim().length > 0);
            }
            
            // If it's an object (not array), get its values and convert to strings
            if (typeof gapsData === 'object' && gapsData !== null) {
                const values = Object.values(gapsData);
                return values
                    .map(toSafeString)
                    .filter(str => str && str.trim().length > 0);
            }
            
            // For primitive types, convert directly
            const result = toSafeString(gapsData);
            return result ? [result] : [];
        } catch (error) {
            console.error('Error parsing gaps data:', error, gapsData);
            return [];
        }
    };

    // Get user-friendly status message
    const getUserFriendlyStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'Waiting to start processing...';
            case 'processing':
                return 'Processing your resume...';
            case 'ENRICHED':
                return 'Resume processed successfully!';
            case 'failed':
                return 'Processing failed';
            default:
                return 'Processing your resume data';
        }
    };

    // eslint-disable-next-line no-unused-vars
    const fetchUser = async (user_id) => {
        setIsLoading(true)
        try {
            const response = await authAxios.get(`/user/${user_id}/`)
            
            // Load additional data (skills from localStorage, experience/education from APIs)
            let skills = [];
            let experience = [];
            let education = [];
            let projects = [];
            
            // Load skills from localStorage
            try {
                const storedSkills = localStorage.getItem('userSkills');
                if (storedSkills) {
                    const parsedSkills = JSON.parse(storedSkills);
                    
                    // Check if any skills are objects (problematic data)
                    const hasObjectSkills = Array.isArray(parsedSkills) && 
                        parsedSkills.some(skill => typeof skill === 'object' && skill !== null);
                    
                    if (hasObjectSkills) {
                        console.warn('Found object skills in localStorage, clearing and starting fresh');
                        localStorage.removeItem('userSkills');
                        skills = [];
                    } else {
                        const skillsArray = Array.isArray(parsedSkills) ? parsedSkills : [];
                        // Ensure all skills are strings
                        skills = skillsArray.map(skill => 
                            typeof skill === 'string' ? skill : (skill?.name || String(skill))
                        );
                    }
                }
            } catch (error) {
                console.error('Error loading skills from localStorage:', error);
                localStorage.removeItem('userSkills');
                skills = [];
            }
            
            try {
                const [experienceResponse, educationResponse, projectsResponse] = await Promise.all([
                    profileAPI.getExperience(user_id),
                    profileAPI.getEducation(user_id),
                    profileAPI.getProjects(user_id)
                ]);
                
                experience = experienceResponse.data.results || [];
                education = educationResponse.data.results || [];
                projects = projectsResponse.data.results || [];
            } catch (apiError) {
                console.error('Error loading profile data from APIs:', apiError);
            }
            
            const enhancedProfile = {
                ...response.data,
                skills: response.data.skills || skills,
                experience: response.data.experience || experience,
                education: response.data.education || education,
                projects: response.data.projects || projects
            };
            
            setUserProfile(enhancedProfile)
            
            // Use direct profile image URL (no protected fetch required)
            if (enhancedProfile?.profile_picture_url) {
                setProfileImageFromUrl(enhancedProfile.profile_picture_url);
            } else {
                setProfileImageUrl(null);
            }
        } catch (error) {
            console.error("Failed to fetch user profile:", error)
            setUserProfile(null)
        }
        setIsLoading(false)
    }

    // Load own profile data with caching
    const loadOwnProfileData = async (skipCache = false) => {
        if (!user?.id) {
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        
        // If skipCache is true, invalidate cache first
        if (skipCache) {
            profileCache.invalidate(user.id);
        }
        
        // Try to load from cache first (will be null if invalidated above)
        const cachedData = profileCache.get(`user_${user.id}`);
        if (cachedData) {
            console.log('Loading profile from cache');
            setUserProfile(cachedData.userProfile);
            setResumeRankings(cachedData.rankings || {});
            
            // Use direct profile image URL from cache
            if (cachedData.userProfile?.profile_picture_url) {
                setProfileImageFromUrl(cachedData.userProfile.profile_picture_url);
            } else {
                setProfileImageUrl(null);
            }
            
            setIsLoading(false);
            return;
        }
        
        // If no cache, fetch from APIs
        let skills = [];
        let experience = [];
        let education = [];
        let projects = [];
        
        // Load skills from localStorage
        try {
            const storedSkills = localStorage.getItem('userSkills');
            if (storedSkills) {
                const parsedSkills = JSON.parse(storedSkills);
                
                // Check if any skills are objects (problematic data)
                const hasObjectSkills = Array.isArray(parsedSkills) && 
                    parsedSkills.some(skill => typeof skill === 'object' && skill !== null);
                
                if (hasObjectSkills) {
                    console.warn('Found object skills in localStorage, clearing and starting fresh');
                    localStorage.removeItem('userSkills');
                    skills = [];
                } else {
                    const skillsArray = Array.isArray(parsedSkills) ? parsedSkills : [];
                    // Ensure all skills are strings
                    skills = skillsArray.map(skill => 
                        typeof skill === 'string' ? skill : (skill?.name || String(skill))
                    );
                }
            }
        } catch (error) {
            console.error('Error loading skills from localStorage:', error);
            localStorage.removeItem('userSkills');
            skills = [];
        }
        
        try {
            const [experienceResponse, educationResponse, projectsResponse] = await Promise.all([
                profileAPI.getExperience(user.id),
                profileAPI.getEducation(user.id),
                profileAPI.getProjects(user.id)
            ]);
            
            experience = experienceResponse.data.results || [];
            education = educationResponse.data.results || [];
            projects = projectsResponse.data.results || [];
        } catch (apiError) {
            console.error('Error loading own profile data from APIs:', apiError);
        }
        
        // Fetch resumes
        let resumes = [];
        try {
            const selfResponse = await authAxios.get('/self/');
            resumes = selfResponse.data?.resumes || [];
        } catch (error) {
            console.error('Error fetching resumes:', error);
        }

        const enhancedUser = {
            ...user,
            skills: user.skills || skills,
            experience: user.experience || experience,
            education: user.education || education,
            projects: user.projects || projects,
            resumes: resumes
        };
        
        setUserProfile(enhancedUser);
        
        // Use direct profile image URL for own profile
        if (enhancedUser?.profile_picture_url) {
            setProfileImageFromUrl(enhancedUser.profile_picture_url);
        } else {
            setProfileImageUrl(null);
        }
        
        // Fetch resume rankings if user has resumes
        let rankings = {};
        if (resumes.length > 0) {
            setRankingsLoading(true);
            try {
                const resumeIds = resumes.map(resume => resume.id);
                rankings = await getMultipleResumeRankings(resumeIds, null);
                setResumeRankings(rankings);
            } catch (error) {
                console.error('Failed to fetch resume rankings:', error);
            } finally {
                setRankingsLoading(false);
            }
        }
        
        // Cache the fetched data
        profileCache.set(`user_${user.id}`, {
            userProfile: enhancedUser,
            rankings: rankings
        });
        
        setIsLoading(false);
    };

    // Update specific sections without full page reload
    const updatePersonalInfo = async (sectionData = null) => {
        try {
            // If section data is provided, update that specific section in state
            if (sectionData && typeof sectionData === 'object') {
                // Check if it's a specific section update (education, experience, projects, etc.)
                if (sectionData.section && sectionData.data) {
                    setUserProfile(prev => ({
                        ...prev,
                        [sectionData.section]: sectionData.data,
                    }));
                    
                    // Update cache
                    if (user?.id) {
                        const cachedData = profileCache.get(`user_${user.id}`);
                        if (cachedData) {
                            profileCache.set(`user_${user.id}`, {
                                ...cachedData,
                                userProfile: {
                                    ...cachedData.userProfile,
                                    [sectionData.section]: sectionData.data,
                                }
                            });
                        }
                    }
                    return;
                }
            }

            // Default behavior: refetch personal info from API
            const response = await profileAPI.getPersonalInfo();
            const updatedUser = response.data;
            
            setUserProfile(prev => ({
                ...prev,
                ...updatedUser,
                // Preserve non-personal info fields
                skills: prev?.skills,
                experience: prev?.experience,
                education: prev?.education,
                projects: prev?.projects,
                resumes: prev?.resumes,
            }));

            // Update profile image if changed
            if (updatedUser?.profile_picture_url) {
                setProfileImageFromUrl(updatedUser.profile_picture_url);
            } else {
                setProfileImageUrl(null);
            }

            // Update cache
            if (user?.id) {
                const cachedData = profileCache.get(`user_${user.id}`);
                if (cachedData) {
                    profileCache.set(`user_${user.id}`, {
                        ...cachedData,
                        userProfile: {
                            ...cachedData.userProfile,
                            ...updatedUser,
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error updating personal info:', error);
            throw error;
        }
    };

    // Effect to handle token query parameter for automatic login
    useEffect(() => {
        const token = searchParams.get('token');
        
        // Only process token if:
        // 1. Token exists in URL
        // 2. User is not already logged in
        // 3. We haven't already processed a token
        // 4. Auth context is done loading
        if (token && !user && !loading && !tokenProcessed) {
            console.log('Processing token from URL for automatic login');
            
            // Save token to localStorage
            localStorage.setItem('token', token);
            
            // Mark as processed to prevent loops
            setTokenProcessed(true);
            
            // Remove token from URL
            searchParams.delete('token');
            setSearchParams(searchParams, { replace: true });
            
            // Trigger user refetch with new token
            if (refetchUser) {
                refetchUser();
            }
        }
    }, [searchParams, user, loading, tokenProcessed, refetchUser, setSearchParams]);

    // Effect to handle URL hash and set initial tab
    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (hash && fragmentToTab.hasOwnProperty(hash)) {
            setSelectedTab(fragmentToTab[hash]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Only proceed if user is loaded (not undefined)
        if (user === undefined) return; // Still loading user from AuthContext
        
        // If no id in URL, show own profile
        // If id in URL and user is recruiter and id != user.id, show other user's profile
        // Otherwise show own profile

        // if (id && user?.is_recruiter && user?.id != id) {   
        //     fetchUser(id)
        // } else {
        //     loadOwnProfileData();
        // }
        loadOwnProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, id])

    if (loading || isLoading) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    backgroundColor: "white",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                }}
            >
                <CircularProgress size={60} sx={{ color: 'primary.main', mb: 3 }} />
                <Typography variant="h6" sx={{ color: 'black', mb: 1 }}>
                    Loading profile...
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Please wait while we fetch the profile information
                </Typography>
            </Box>
        );
    }
    
    // if (!loading && id && !user?.is_recruiter && user?.id !== id) {
    if (!loading && id && user?.id !== id) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    px: 2,
                }}
            >
                <Typography variant="h5" color="error">
                    ❌ You do not have permission to view this page.
                </Typography>
            </Box>
        );
    }

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        // Update URL hash
        const fragment = tabFragments[newValue];
        if (fragment) {
            window.history.pushState(null, '', `#${fragment}`);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "white",
                py: 4,
                px: 2,
            }}
        >
            <Container maxWidth="xl">
                <Paper sx={{ 
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: 'none',
                    border: '1px solid #e5e7eb',
                    p: { xs: 3, sm: 4, md: 5 },
                    mb: 4,
                    position: 'relative'
                }}>
                    {/* Profile Header Section - Mercor Style */}
                    <Box sx={{ mb: 5 }}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 3, 
                            alignItems: { xs: 'center', sm: 'center' } 
                        }}>
                            {/* Profile Picture */}
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    src={profileImageUrl}
                                    sx={{
                                        width: { xs: 80, sm: 100 },
                                        height: { xs: 80, sm: 100 },
                                        fontSize: { xs: 32, sm: 40 },
                                        bgcolor: "#4f46e5",
                                        border: profileImageUrl ? "3px solid white" : "3px solid #fca5a5",
                                        boxShadow: profileImageUrl 
                                            ? '0 4px 14px rgba(0,0,0,0.1)' 
                                            : '0 0 0 4px rgba(252, 165, 165, 0.4), 0 4px 14px rgba(0,0,0,0.1)',
                                        flexShrink: 0
                                    }}
                                >
                                    {!profileImageUrl && !imageLoading && getInitials(userProfile?.first_name, userProfile?.last_name)}
                                    {imageLoading && '...'}
                                </Avatar>
                                {/* Camera Icon - Only show if viewing own profile */}
                                {(!id || (user?.id === id)) && (
                                    <IconButton
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            position: 'absolute',
                                            bottom: -4,
                                            right: -4,
                                            bgcolor: '#4f46e5',
                                            color: 'white',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                bgcolor: '#4338ca',
                                            },
                                            boxShadow: 1,
                                        }}
                                    >
                                        <CameraAltIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    style={{ display: 'none' }}
                                />
                            </Box>
                            
                            {/* Name */}
                            <Box sx={{ 
                                flex: 1, 
                                textAlign: { xs: 'center', sm: 'left' },
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                <Typography 
                                    variant="h4" 
                                    fontWeight={600} 
                                    sx={{ 
                                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                        color: '#111827',
                                        letterSpacing: '-0.025em'
                                    }}
                                >
                                    {userProfile?.first_name} {userProfile?.middle_name ? userProfile.middle_name + ' ' : ''}{userProfile?.last_name}
                                </Typography>
                                {userProfile?.email && (
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {userProfile.email}
                                    </Typography>
                                )}
                            </Box>
                            
                            {/* Resume Score/Grade or Upload Button */}
                            {(() => {
                                // Get the latest resume
                                const latestResume = userProfile?.resumes?.[0];
                                
                                if (!latestResume) {
                                    // No resume uploaded - show upload button
                                    return (
                                        <Box sx={{ 
                                            textAlign: { xs: 'center', sm: 'right' },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                No resume uploaded yet
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="large"
                                                onClick={() => resumeInputRef.current?.click()}
                                                disabled={uploadingResume}
                                                startIcon={uploadingResume ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: { xs: 2, sm: 4 },
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    width: { xs: '100%', sm: 'auto' },
                                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                                    '&:hover': {
                                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                                    },
                                                    '&:disabled': {
                                                        opacity: 0.6,
                                                    }
                                                }}
                                            >
                                                {uploadingResume ? 'Uploading...' : 'Upload Your First Resume'}
                                            </Button>
                                            {uploadStatus && (
                                                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                                                    {uploadStatus}
                                                </Typography>
                                            )}
                                            <input
                                                type="file"
                                                ref={resumeInputRef}
                                                onChange={handleResumeFileSelect}
                                                accept="application/pdf"
                                                style={{ display: 'none' }}
                                            />
                                        </Box>
                                    );
                                }

                                // Get ranking for latest resume
                                const ranking = resumeRankings[latestResume.id];
                                console.log('[HEYHEY] Latest resume rankings:', ranking);
                                let latestRanking = null;
                                
                                if (ranking && ranking.rankings) {
                                    const generalRankings = ranking.rankings.filter(r => r.application_id === null);
                                    if (generalRankings.length > 0) {
                                        const sortedRankings = generalRankings.sort((a, b) => 
                                            new Date(b.created_at) - new Date(a.created_at)
                                        );
                                        latestRanking = sortedRankings[0].ranking;
                                    }
                                }

                                if (rankingsLoading) {
                                    return (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: { xs: 'center', sm: 'flex-start' },
                                            gap: 2,
                                            width: { xs: '100%', sm: 'auto' }
                                        }}>
                                            <CircularProgress size={30} />
                                            <Typography variant="body2" color="text.secondary">
                                                Loading resume score...
                                            </Typography>
                                        </Box>
                                    );
                                }

                                if (latestRanking) {
                                    return (
                                        <Box sx={{ 
                                            textAlign: { xs: 'center', sm: 'right' },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                                Latest Resume Score
                                            </Typography>
                                            <GradeScoreDisplay 
                                                grade={latestRanking.grade} 
                                                score={latestRanking.raw_score}
                                                size="large"
                                                showLabels={true}
                                            />
                                        </Box>
                                    );
                                }

                                // Resume exists but no ranking yet
                                return (
                                    <Box sx={{ 
                                        textAlign: { xs: 'center', sm: 'right' },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Resume score pending...
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </Box>
                    </Box>

                    {/* Tabs Navigation - Mercor-style */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                        <Tabs 
                            value={selectedTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    minWidth: { xs: 80, sm: 100 },
                                    px: { xs: 1.5, sm: 2 },
                                    color: '#6b7280',
                                    '&.Mui-selected': {
                                        color: '#4f46e5',
                                        fontWeight: 600,
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#4f46e5',
                                    height: 2,
                                },
                            }}
                        >
                            <Tab label="Resume" />
                            <Tab label="Location & Work Authorization" />
                            <Tab label="Availability" />
                            <Tab label="Work Preferences" />
                            <Tab label="Communications" />
                            <Tab label="Account" disabled sx={{ opacity: 0.5, cursor: 'not-allowed' }} />
                        </Tabs>
                    </Box>

                    {/* Tab Content - Mercor-style components */}
                    <Box sx={{ py: { xs: 2, sm: 3 } }}>
                        {selectedTab === 0 && <ResumeTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} onTriggerUpload={handleResumeFileSelect} parsedDataToApply={parsedDataForResumeTab} />}
                        {selectedTab === 1 && <LocationWorkAuthTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} />}
                        {selectedTab === 2 && <AvailabilityTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} />}
                        {selectedTab === 3 && <WorkPreferencesTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} />}
                        {selectedTab === 4 && <CommunicationsTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} />}
                        {selectedTab === 5 && <AccountTab userProfile={userProfile} userId={user?.id} authAxios={authAxios} onUpdate={updatePersonalInfo} setError={setError} />}
                    </Box>
                </Paper>
            </Container>

            {/* Profile Picture Preview Dialog */}
            <Dialog open={showPreview} onClose={() => !uploading && setShowPreview(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Profile Picture</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                        <Avatar
                            src={previewUrl}
                            sx={{
                                width: 200,
                                height: 200,
                                border: '4px solid',
                                borderColor: 'primary.main',
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            Maximum file size: 5 MB • Supported formats: JPG, PNG, WebP
                        </Typography>
                        {profileImageUrl && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteProfilePicture}
                                disabled={uploading}
                            >
                                Remove Current Picture
                            </Button>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPreview(false)} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUploadProfilePicture}
                        disabled={uploading}
                    >
                        {uploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resume Upload Modal */}
            <Dialog
                open={showResumeUploadModal}
                onClose={() => {
                    // Only allow closing if not in processing states
                    if (resumeModalStep !== 'parsing' && resumeModalStep !== 'analyzing' && resumeModalStep !== 'uploading') {
                        setShowResumeUploadModal(false);
                        setResumeModalStep('initial');
                    }
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Upload Resume
                    {uploadingResume && <LinearProgress sx={{ mt: 1 }} />}
                </DialogTitle>
                <DialogContent sx={{ minHeight: 300 }}>
                    {/* Uploading State */}
                    {resumeModalStep === 'uploading' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={60} sx={{ mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Uploading Resume...
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please wait while we upload your resume
                            </Typography>
                        </Box>
                    )}

                    {/* Parsing State */}
                    {resumeModalStep === 'parsing' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={60} sx={{ mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Parsing Resume...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {getUserFriendlyStatus(resumeStatus?.status) || 'Processing your resume data'}
                            </Typography>
                            <Button
                                startIcon={<RefreshIcon />}
                                onClick={async () => {
                                    if (currentResumeId) {
                                        try {
                                            const response = await authAxios.get(`/user-resume/${currentResumeId}/status/`);
                                            const status = response.data;
                                            setResumeStatus(status);
                                            if (status.status === 'ENRICHED') {
                                                setResumeModalStep('analyzing');
                                                await fetchParsedDataAndRank(currentResumeId);
                                                setResumeModalStep('completed');
                                            } else if (status.status === 'failed') {
                                                setError('Resume processing failed. Please try again.');
                                                setShowResumeUploadModal(false);
                                                setResumeModalStep('initial');
                                            }
                                        } catch (error) {
                                            console.error('Error refreshing status:', error);
                                            const errorMessage =
                                                error.response?.data?.detail ||
                                                error.response?.data?.message ||
                                                error.response?.data?.error ||
                                                'Failed to refresh status';
                                            setError(errorMessage);
                                        }
                                    }
                                }}
                                sx={{ mt: 2 }}
                            >
                                Refresh Status
                            </Button>
                        </Box>
                    )}

                    {/* Analyzing State */}
                    {resumeModalStep === 'analyzing' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={60} sx={{ mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Analyzing your resume
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                This may take a while
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                We're calculating your resume score and providing detailed insights...
                            </Typography>
                        </Box>
                    )}

                    {/* Completed State */}
                    {resumeModalStep === 'completed' && resumeRankingData && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Resume Uploaded Successfully!
                            </Typography>

                            {/* Resume Evaluation */}
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 2,
                                    bgcolor: 'primary.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'primary.200',
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                                    Resume Evaluation
                                </Typography>
                                
                                {/* Grade and Score Display */}
                                <Box sx={{ mb: 3 }}>
                                    <GradeScoreDisplay 
                                        grade={resumeRankingData.grade} 
                                        score={resumeRankingData.raw_score}
                                        size="medium"
                                        showLabels={true}
                                    />
                                </Box>

                                {/* Justification */}
                                {resumeRankingData.justification && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Assessment Summary
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {resumeRankingData.justification}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Areas for Improvement */}
                                {resumeRankingData.gaps_compared_to_target_job && (
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Areas for Improvement
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                            {parseGapsData(resumeRankingData.gaps_compared_to_target_job).map((gap, index) => (
                                                <Typography
                                                    component="li"
                                                    key={index}
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary', mb: 0.5 }}
                                                >
                                                    {gap}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Parsed Data Preview - Show after completion */}
                    {resumeModalStep === 'completed' && parsedData && parsedData.parsed_data && (
                        <Box sx={{ mt: 3 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                Extracted Information Preview:
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    bgcolor: 'grey.50',
                                    p: 2,
                                    borderRadius: 1,
                                }}
                            >
                                <Stack spacing={2}>
                                    {/* Name and Contact */}
                                    <Box>
                                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                            Personal Information
                                        </Typography>
                                        <Typography variant="body2">
                                            Name: {parsedData.parsed_data.name}
                                        </Typography>
                                        <Typography variant="body2">
                                            Email: {parsedData.parsed_data.contact?.email}
                                        </Typography>
                                        <Typography variant="body2">
                                            Phone: {parsedData.parsed_data.contact?.phone}
                                        </Typography>
                                    </Box>

                                    {/* Skills */}
                                    {parsedData.parsed_data.skills && parsedData.parsed_data.skills.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                                Skills ({parsedData.parsed_data.skills.length})
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {parsedData.parsed_data.skills.slice(0, 10).map((skill, idx) => (
                                                    <Chip key={idx} label={skill} size="small" variant="outlined" />
                                                ))}
                                                {parsedData.parsed_data.skills.length > 10 && (
                                                    <Chip label={`+${parsedData.parsed_data.skills.length - 10} more`} size="small" />
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Experience */}
                                    {parsedData.parsed_data.experience && parsedData.parsed_data.experience.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                                Work Experience ({parsedData.parsed_data.experience.length})
                                            </Typography>
                                            {parsedData.parsed_data.experience.slice(0, 3).map((exp, idx) => (
                                                <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {exp.title} at {exp.company}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {exp.start_date} - {exp.end_date}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            {parsedData.parsed_data.experience.length > 3 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    +{parsedData.parsed_data.experience.length - 3} more positions
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {/* Education */}
                                    {parsedData.parsed_data.education && parsedData.parsed_data.education.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                                Education ({parsedData.parsed_data.education.length})
                                            </Typography>
                                            {parsedData.parsed_data.education.map((edu, idx) => (
                                                <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {edu.degree}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {edu.institution} - {edu.end_date}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Projects */}
                                    {parsedData.parsed_data.projects && parsedData.parsed_data.projects.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                                Projects ({parsedData.parsed_data.projects.length})
                                            </Typography>
                                            {parsedData.parsed_data.projects.slice(0, 5).map((project, idx) => (
                                                <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {project.name || project}
                                                    </Typography>
                                                    {project.technologies && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Technologies: {project.technologies}
                                                        </Typography>
                                                    )}
                                                    {project.description && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                            {project.description.length > 100 ? `${project.description.substring(0, 100)}...` : project.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                            {parsedData.parsed_data.projects.length > 5 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    +{parsedData.parsed_data.projects.length - 5} more projects
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {resumeModalStep === 'completed' && parsedData && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                // Pass parsed data to ResumeTab to populate form fields (NO API calls)
                                // User will need to click "Save Profile" in ResumeTab to persist changes
                                setParsedDataForResumeTab(parsedData);
                                
                                // Close modal
                                setShowResumeUploadModal(false);
                                setResumeModalStep('initial');
                                setResumeRankingData(null);
                                setParsedData(null);
                                
                                // Navigate to Resume tab if not already there
                                setSelectedTab(0);
                                
                                // Show info message
                                setError('Resume data applied to form. Click "Save Profile" to save your changes.');
                            }}
                            disabled={applyingToProfile}
                            startIcon={applyingToProfile ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            Apply to Profile
                        </Button>
                    )}
                    {resumeModalStep === 'completed' && !parsedData && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setShowResumeUploadModal(false);
                                setResumeModalStep('initial');
                                setResumeRankingData(null);
                            }}
                        >
                            Done
                        </Button>
                    )}
                    {resumeModalStep !== 'completed' && resumeModalStep !== 'uploading' && resumeModalStep !== 'parsing' && resumeModalStep !== 'analyzing' && (
                        <Button
                            onClick={() => {
                                setShowResumeUploadModal(false);
                                setResumeModalStep('initial');
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProfileComponent;